import type { ReactNode } from "react"

import type { CustomDataTarget } from "@productlane/api/src/browser"
import { cn } from "@productlane/lib/src/cn"
import { ScrollArea, ScrollViewport } from "@productlane/ui"

import { CustomSidebarSections } from "@/screens/app/threads/components/right-bar/custom-sidebar-sections"
import { useSidebarConnectors } from "@/screens/app/threads/components/right-bar/sidebar-sections/use-sidebar-connectors"

interface Props {
	target: CustomDataTarget
	className?: string
}

// The thread right-bar's connector list, on the contact and company detail pages.
export function CustomConnectorsSidebar({ target, className }: Props) {
	const { sections } = useSidebarConnectors()

	// Only visible HTTP connectors draw cards, so no other kind opens an empty column.
	if (sections.length === 0) return null

	return (
		<div
			className={cn(
				"hidden h-full w-[20rem] flex-none border-l border-gray-200 bg-gray-100 lg:block dark:border-gray-700/80 dark:bg-gray-850",
				className,
			)}
		>
			<ScrollArea>
				<ScrollViewport>
					<CustomSidebarSections target={target} className="px-2 pb-8" />
				</ScrollViewport>
			</ScrollArea>
		</div>
	)
}

// Puts the connector column beside the page's own top bar rather than under it,
// so it runs the full height of the screen.
export function DetailScreenWithConnectors({
	target,
	enabled = true,
	children,
}: {
	target: CustomDataTarget
	enabled?: boolean
	children: ReactNode
}) {
	return (
		<div className="flex min-h-0 flex-1 overflow-hidden">
			<div className="flex min-w-0 flex-1 flex-col overflow-hidden">
				{children}
			</div>
			{enabled ? <CustomConnectorsSidebar target={target} /> : null}
		</div>
	)
}
